import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Progress, Row } from 'reactstrap';
import BreadcrumbTrail from '../../../../../../Common/BreadcrumbTrail';
import { useHistory } from 'react-router-dom';
import { ChevronLeft, X } from 'react-feather';

import mapVendor from '../../../../../../../assets/images/map-vendor.svg';
import { MdOutlineCloudUpload } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { DOWNLOAD_PRICE_FILE, DOWNLOAD_PRICE_FILE_EMPTY, UPLOAD_PRICE_FILE, UPLOAD_PRICE_FILE_EMPTY } from '../../../../../../../store/actions';
import { FaFileExcel } from 'react-icons/fa';
import TypeCard from '../Comman/TypeCard';

const UploadClientPricing = ({ state, setState, setFormCount }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [clientPricingRows, setClientPricingRows] = useState([]);
  const [progress, setProgress] = useState(0);

  const { downloadPriceType, uploadPriceType, uploadPriceTypeLoading } = useSelector((state: any) => ({
    downloadPriceType: state.DownloadPriceType.data,

    uploadPriceType: state.UploadPriceType.data,
    uploadPriceTypeLoading: state.UploadPriceType.loading,
  }));

  // Simulate progress bar when loading
  useEffect(() => {
    if (uploadPriceTypeLoading) {
      let p = 0;
      const interval = setInterval(() => {
        p += 5;
        if (p >= 100) {
          clearInterval(interval);
          dispatch({ type: DOWNLOAD_PRICE_FILE_EMPTY });
        } else {
          setProgress(p);
        }
      }, 300);

      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [uploadPriceTypeLoading, dispatch]);

  useEffect(() => {
    if (uploadPriceType !== null) {
      handleUploadDataUpdate(uploadPriceType?.results);
    }
  }, [uploadPriceType]);

  const generateFileName = () => {
    const clientName = state?.client?.name;
    const today = new Date();

    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    return `AT_${clientName}_Price_${formattedDate}${state?.version ? `_v${state?.version}` : ''}.xlsx`;
  };

  useEffect(() => {
    if (downloadPriceType) {
      const blob = new Blob([downloadPriceType], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = generateFileName();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      dispatch({
        type: DOWNLOAD_PRICE_FILE_EMPTY,
      });
    }
  }, [downloadPriceType]);

  const makeRows = (airports, client, benefitConfig) => {
    const result = [];

    airports?.forEach(airport => {
      airport.zones?.forEach(zone => {
        const hasZone = client.zones?.some(vz => vz.id === zone?.id && vz.airport?.id === airport?.id);
        if (hasZone) {
          if (client.carTypes && client.carTypes.length > 0) {
            client.carTypes.forEach(carType => {
              ['Pickup', 'Drop'].forEach(serviceType => {
                benefitConfig?.forEach(benefit => {
                  result.push({
                    id: `${airport?.id}:${zone?.id}:${client?.id}:${carType?.id}:${serviceType}:${client?.currency}:${benefit?.id}`,
                    airport: {
                      id: airport?.id,
                      name: airport?.name,
                    },
                    zone: { id: zone?.id, name: zone?.name },
                    client: {
                      id: client?.id,
                      name: client?.name,
                    },
                    carType: {
                      id: carType?.id,
                      name: carType?.name,
                    },
                    serviceType,
                    currency: client?.currency || airport?.currency || 'AED',
                    benefitGroupConfig: benefit,
                  });
                });
              });
            });
          }
        }
      });
    });

    return result;
  };

  useEffect(() => {
    if (state?.clientPricing?.pricingDetails?.length > 0) {
      const newClientRows = makeRows(state?.clientPricing?.airports, state?.client, state?.benefitGroup?.benefit_configs ?? []);
      const existingClientRowsMap = new Map(state?.clientPricing?.pricingDetails?.map(item => [item.id, item]));
      const combineAlltheRows = newClientRows.map(item => {
        const commanRows = existingClientRowsMap.get(item.id);
        if (commanRows) {
          return {
            ...item, // keep existing keys in a
            ...commanRows, // overwrite/add from b
          };
        }
        return item;
      });
      setClientPricingRows(combineAlltheRows);
    } else {
      const getRows = makeRows(state?.clientPricing?.airports, state?.client, state?.benefitGroup?.benefit_configs ?? []);
      setClientPricingRows(getRows ?? []);
    }
  }, []);

  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) {
      history.push(`/dashboard`);
    }
  };

  const handleSubmit = () => {
    setState(prev => ({
      ...prev,
      clientPricing: {
        ...prev?.clientPricing,
        pricingDetails: clientPricingRows,
      },
    }));
    setFormCount(prev => prev + 1);
  };

  const handleUploadDataUpdate = updatedRows => {
    const pricingDetailsSummary = clientPricingRows?.map(row => {
      const match = updatedRows?.find(item => item?.id === row?.id);
      if (match) {
        const basePriceChanged = row.basePrice !== match.basePrice;
        const parkingFeeChanged = row.parkingFee !== match.parkingFee;
        const discountChanged = row.discount !== match.discount;
        const taxChanged = row.tax !== match.tax;

        if (basePriceChanged || parkingFeeChanged || discountChanged || taxChanged) {
          return {
            ...row,
            basePrice: match.basePrice,
            parkingFee: match.parkingFee,
            discount: match.discount,
            tax: match.tax,
            ...(row.hasOwnProperty('tag') && row?.tag === 'default' ? { tag: 'updated' } : {}), // only add if tag exists
          };
        } else {
          return {
            ...row,
            basePrice: match.basePrice,
            parkingFee: match.parkingFee,
            discount: match.discount,
            tax: match.tax,
          };
        }
      }

      return row;
    });

    setState(prev => ({
      ...prev,
      clientPricing: {
        ...prev?.clientPricing,
        pricingDetails: pricingDetailsSummary,
      },
    }));

    setFormCount(prev => prev + 1);

    dispatch({
      type: UPLOAD_PRICE_FILE_EMPTY,
    });
  };

  const fileHandler = async e => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    dispatch({
      type: UPLOAD_PRICE_FILE,
      payload: {
        data: formData,
        priceType: 'client-price-file',
      },
    });

    e.target.value = null;
  };

  return (
    <div className="page-content">
      <Container fluid className="position-relative">
        <div className="d-flex justify-content-between align-items-center">
          <BreadcrumbTrail
            title={`Airport Configuration Setup`}
            navigationby={breadcrumNavigationHandler}
            navigation={['Management', 'Program', 'Onboarding', 'Upload Client Pricing']}
          />
        </div>

        <Card>
          <CardHeader className="d-flex justify-content-between p-0">
            <h5 className="m-0 fw-semibold px-3">Client Pricing Upload</h5>
            <TypeCard typeName={'Client'} name={state?.client?.name} />
          </CardHeader>
          <CardBody>
            <Row className="mb-3">
              <Col md="7">
                <h4 className="mb-4 text-primary fw-medium">Upload File</h4>

                <div
                  className="upload-box mb-2 position-relative"
                  onClick={() => document.getElementById('fileInput').click()}
                  style={{ cursor: 'pointer' }}
                >
                  <input type="file" id="fileInput" accept=".xlsx" style={{ display: 'none' }} onChange={fileHandler} />
                  <MdOutlineCloudUpload size={40} className="mb-2 text-primary" />
                  <p className="mb-1">
                    Drag your file(s) or <span className="browse-text">browse</span>
                  </p>
                  <small className="text-muted">Supported format: .xlsx</small>
                </div>

                <p
                  className="text-primary fw-semibold text-end text-decoration-underline cursor-pointer"
                  onClick={() => {
                    dispatch({
                      type: DOWNLOAD_PRICE_FILE,
                      payload: {
                        priceType: 'client-price-file',
                        data: clientPricingRows,
                      },
                    });
                  }}
                >
                  Click here to download your configured file and add pricing.
                </p>

                <div className="file-rules-card">
                  <p className="px-3 pt-3 fw-semibold">File Rules</p>
                  <div className="px-3 pb-2">
                    <ul className="list-unstyled mb-0 gap-1">
                      {['File type: .xlsx only', 'Maximum file size: 5MB', 'Maximum rows: 10,000', 'File Version - V2.0'].map((rule, idx) => (
                        <li key={idx} className="mb-2 d-flex align-items-center gap-2">
                          <i className="ri-check-fill" style={{ color: 'rgba(88, 109, 177, 1)' }}></i> <p className="m-0">{rule}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {uploadPriceTypeLoading && (
                  <FileDownloadCard
                    filename="demo.xls"
                    size="500kb"
                    progress={progress}
                    onCancel={() => dispatch({ type: DOWNLOAD_PRICE_FILE_EMPTY })}
                  />
                )}
              </Col>

              <Col md="5">
                <div className="position-relative border border-radius-5 p-3">
                  <h5>Client Pricing</h5>
                  <p className="text-muted small">Upload pricing here to map them to clients</p>
                  <div className="vendor-mapping-list">
                    <Card
                      className="mb-3 cursor-pointer p-2"
                      style={{
                        border: '1px dashed #D1D1D1',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between cursor-pointer" onClick={() => setIsCollapse(!isCollapse)}>
                        <div className="d-flex flex-column pt-2 px-2">
                          <div className="d-flex gap-1 align-items-start">
                            <img className="mt-3" src={mapVendor} alt="" />
                            <div className="mt-2">
                              <div className="d-flex gap-2 align-items-center">
                                <h6 className="m-0 fw-medium fs-14">{state?.client?.name || ''}</h6>
                                <p
                                  style={{
                                    color: '#0AB39C',
                                    border: '1px solid #0AB39C',
                                    borderRadius: '20px',
                                    fontSize: '6px',
                                  }}
                                  className="m-0 pt-1 pb-1 px-2 pe-2"
                                >
                                  Full Configured
                                </p>
                              </div>
                              <div className="d-flex gap-2 mt-1">
                                <p className="text-muted small m-0">{state?.client?.contracting_country?.name ?? ''}</p>
                                <div className="d-flex flex-wrap gap-1">
                                  {state?.client?.carTypes?.map((car, idx) => (
                                    <p
                                      key={idx}
                                      style={{
                                        borderRadius: '20px',
                                        padding: '5px 5px',
                                        fontSize: '8px',
                                        border: '1px solid #D0D2DC',
                                        color: '#6F7594',
                                      }}
                                      className="m-0"
                                    >
                                      {car?.name?.split(' ')[0]}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {state?.client?.zones?.map(zone => (
                        <div
                          key={zone?.id}
                          className="d-flex justify-content-between align-items-center p-2 m-1 rounded-2"
                          style={{
                            border: '1px solid rgba(161, 229, 247, 1)',
                            background: 'rgba(236, 250, 253, 1)',
                          }}
                        >
                          <div
                            className="d-flex flex-column"
                            style={{
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <p className="fw-medium m-0 fs-12" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {zone?.name}
                            </p>
                            <p className="fw-light m-0 fs-12" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {zone?.airport?.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </Card>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="d-flex justify-content-between">
          <Button
            onClick={() => setFormCount(prev => prev - 1)}
            color="light"
            className="d-flex align-items-center gap-1 px-3 py-2"
            style={{ backgroundColor: '#E9EBEC', color: '#404B7C', fontWeight: 500, boxShadow: 'none', border: 'none' }}
          >
            <ChevronLeft size={18} /> Back
          </Button>
          <Button
            className=""
            style={{ background: 'rgba(255, 255, 255, 1)', color: 'rgba(64, 81, 137, 1)', width: '120px' }}
            color="primary"
            onClick={handleSubmit}
          >
            Skip
          </Button>
        </div>
      </Container>
    </div>
  );
};

const FileDownloadCard = ({ filename = 'demo.xls', size = '500kb', progress = 60, onCancel }) => {
  return (
    <Card
      className="p-2 d-flex flex-row align-items-center justify-content-between mt-3"
      style={{ borderRadius: '1rem', border: '1px solid rgba(231, 231, 231, 1)' }}
    >
      <Row className="align-items-center w-100">
        <Col xs="auto">
          <FaFileExcel size={40} color="#217346" />
        </Col>
        <Col className="ms-2">
          <div style={{ fontWeight: 600 }}>{filename}</div>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            {size}
          </div>
          <Progress value={progress} color="primary" className="mt-2" style={{ height: '8px', borderRadius: '10px' }} />
        </Col>
        <Col xs="auto">
          <X size={18} onClick={onCancel} style={{ cursor: 'pointer' }} />
        </Col>
      </Row>
    </Card>
  );
};

export default UploadClientPricing;
